import { CRAYO_ENTRY, crayoPlan, money, crayoPlanTerms } from "@/lib/compare/crayo";
import type { CompareContent } from "@/lib/compare/types";

const hobby = CRAYO_ENTRY;
const clipper = crayoPlan("Clipper");

export const DESCRIPT_CONTENT: CompareContent = {
  slug: "crayo-vs-descript",
  competitorSlug: "descript",
  title: "Crayo vs Descript",
  seoTitle: "Crayo vs Descript: Clipping Engine vs Text Editor, Compared",
  seoDescription:
    "Crayo vs Descript, dated prices. Descript meters media hours plus AI credits and crops by hand. Crayo charges 1 credit per finished clip, reframing included.",
  cardVerdict:
    "Better than Crayo for long-form text editing and desktop apps, behind on reframing and metering.",
  bestFor: "Clippers turning podcast episodes into shorts",
  datePublished: "2026-10-09",
  dateModified: "2026-10-25",
  verdict:
    `As of October 25, 2026, Crayo is the better pick for clippers turning a podcast or interview into shorts: AutoClip ranks the moments, reframes faces to 9:16 and charges one credit per finished clip, ${money(hobby.monthly)} a month for ${hobby.credits} clips with cleanup included. Descript is the better pick if you edit whole episodes by editing the transcript, or need Mac or Windows apps, voice clones or 4K. The evidence follows.`,
  pickCrayoIf: [
    "You clip podcasts and interviews for Reels, Shorts and TikTok: AutoClip finds and ranks the moments, reframes faces to 9:16 and cuts 1 to 20 clips per run. Descript has no automatic reframing and crops by hand.",
    "You want one meter: Crayo charges 1 credit per finished clip, with filler-word and silence cuts included as AutoClip layers, while Descript meters media hours plus AI credits for every Underlord action.",
    `You want the timeline editor, 32 caption styles in 56 spoken languages and a 34-endpoint API on the ${money(hobby.monthly)} entry plan, with no watermark on any export.`,
  ],
  pickThemIf: [
    "You edit whole episodes by editing text. Descript's transcript editor, Studio Sound and Multicam are built for long-form, and Crayo's editor is a timeline, not a text editor.",
    "You need a native Mac or Windows app, voice clones, or 4K export. Crayo is a browser app, has no voice cloning and exports at 1080p.",
    "You need team seats, a free tier or enterprise compliance: Creator carries 1 to 3 seats, Business 5, Enterprise adds SSO, SCIM and SOC 2.",
  ],
  wins: [
    {
      claim: "Crayo reframes faces automatically; Descript does not.",
      evidence:
        "Descript's product pages describe manual crop and drag for vertical output, with Eye Contact and Multicam as its camera tools, checked Oct 8, 2026. Crayo's AutoClip runs a face-aware camera track on every clip and the editor exposes the same track with keyframes.",
      href: "https://www.descript.com/clips",
    },
    {
      claim: "Crayo meters by finished clip; Descript meters media hours plus AI credits.",
      evidence:
        "Descript Hobbyist includes 10 media hours and 400 AI credits a month for $24, and Underlord, Studio Sound, Green Screen, Eye Contact and generated media all draw credits, per its pricing page on Oct 8, 2026. A third-party pricing history dates the switch to media hours plus credits to September 2025. Crayo charges 1 workflow credit per clip and nothing for the editing layers.",
      href: "https://www.descript.com/pricing",
    },
    {
      claim: "Crayo's filler and silence cuts are free layers, not metered extras.",
      evidence:
        "In AutoClip, filler cuts and silence cuts are two of the ten switchable layers, included in the clip's single credit. One Trustpilot reviewer on Oct 2, 2026 reported spending 800 Descript credits on filler-pause cleanup; the quote is linked in the billing section.",
      href: "https://crayo.ai/features/auto-clip",
    },
    {
      claim: "Crayo runs in the browser with nothing to install and exports to 1080p on the entry plan.",
      evidence:
        "Descript ships Mac (macOS 14 or later) and Windows 11 apps alongside its web editor, and its Free plan exports 720p with a watermark. Crayo has no free plan, but every paid plan exports 1080p without a watermark.",
      href: "https://www.descript.com/download",
    },
  ],
  concessions: [
    {
      claim: "Descript is the stronger long-form, text-based editor.",
      evidence:
        "Deleting a sentence in the transcript deletes it from the video, and Underlord, Studio Sound and Multicam work on whole episodes. Crayo's editor is a timeline with a camera track and caption styles, built for clips, not full podcasts.",
      href: "https://www.descript.com",
    },
    {
      claim: "Descript has native desktop apps, voice clones and 4K export.",
      evidence:
        "Mac (macOS 14+) and Windows 11 apps are on the download page, voice cloning is included, and 4K export starts on Creator at $35 a month or $24 a month billed annually, checked Oct 8, 2026. Crayo is browser-only, has no cloning and tops out at 1080p.",
      href: "https://www.descript.com/download",
    },
    {
      claim: "Descript has a free tier, team seats and enterprise compliance.",
      evidence:
        "Free gives 1 media hour a month at 720p with a watermark. Creator carries 1 to 3 seats, Business up to 5 seats with an SLA, and Enterprise adds SSO, SCIM and SOC 2. Crayo has no free plan and no team seats.",
      href: "https://www.descript.com/pricing",
    },
    {
      claim: "Descript's API ships with an MCP server.",
      evidence:
        "Descript publishes an API and an MCP server at descript.com/api and descript.com/mcp. Crayo's API has 34 endpoints and webhooks on every plan but no MCP server as of Oct 25, 2026.",
      href: "https://www.descript.com/api",
    },
  ],
  typicalMonth: {
    scenario: "One 60-minute source a week, eight clips each: 240 source minutes and 32 clips a month.",
    crayo: {
      plan: "Hobby",
      cost: `${money(hobby.monthly)}/mo or about ${money(hobby.yearlyPerMonth)}/mo yearly`,
      usage: `32 of ${hobby.credits} workflow credits and about 32 of ${hobby.exportMinutes} export minutes`,
      note: "Prices from the live plan table. Filler and silence cuts cost nothing extra.",
    },
    them: {
      plan: "Hobbyist",
      cost: "$24/mo or $16/mo yearly, per person",
      usage: "4 of 10 media hours; clips made with Underlord also spend AI credits from the 400 monthly pool",
      note: "Descript does not publish a credit cost per clip, so the credit side cannot be totalled.",
    },
  },
  test: {
    status: "scheduled",
    source: "A 45-minute two-person podcast recording owned by Crayo",
    plans: "Descript Hobbyist and Crayo Clipper, same file, same day, defaults first then one tuned run each",
    notes: [
      "The test is a podcast episode to five shorts on each side, recording wall time, clips judged usable, credits spent on each metering model and whether the speaker stayed framed.",
    ],
  },
  billingNote:
    "Descript bills per person and meters two pools at once, media hours and AI credits; the Free plan's 100 credits are a one-time grant. Refunds are granted only within 48 hours of the invoice with no proration, per Descript's refund policy.",
  faqs: [
    {
      question: "Is Crayo cheaper than Descript?",
      answer:
        `On sticker price it depends on the term; on clips, usually. Crayo Hobby is ${crayoPlanTerms(hobby)} for ${hobby.credits} workflow credits, and one credit is one finished clip. Descript Hobbyist is $24 a month or $16 a month billed annually, per person, for 10 media hours and 400 AI credits, so Descript is lower on monthly billing, Crayo is lower on quarterly billing, and the two are within a dollar a month on yearly billing. The media hours cover four hour-long episodes with room to spare, but every Underlord action, Studio Sound pass and generated image draws from the credit pool, and Descript does not publish a credit cost per clip. If you edit full episodes in text and clip rarely, Descript's hour-based pool is the fairer deal.`,
    },
    {
      question: "Does Crayo have a free plan like Descript?",
      answer:
        `No. Crayo has no free plan and no trial. The entry plan is Hobby at ${money(hobby.monthly)} a month, and the only free products are twelve browser tools at crayo.ai/features/free that need no account and never upload your file. Descript's Free plan includes 1 media hour a month, 100 AI credits granted once, 720p export with a watermark and 5 GB of storage, so it is a real way to try the text editor before paying.`,
    },
    {
      question: "What did Descript's pricing change to?",
      answer:
        "Descript now meters two pools: media hours uploaded per month and AI credits spent on Underlord, Studio Sound, Green Screen, Eye Contact and generated media. On Oct 8, 2026 the tiers were Free at 1 hour and 100 one-time credits, Hobbyist at 10 hours and 400 credits for $24, Creator at 30 hours and 800 credits for $35, and Business at 40 hours and 1,500 credits for $65, all per person with lower annual rates. A third-party pricing history dates the move to September 2025. Crayo meters one pool, workflow credits, at one credit per finished clip.",
    },
    {
      question: "Does Descript have auto reframe like Crayo?",
      answer:
        "No. As of Oct 8, 2026, Descript's clip and layout tools crop vertical video by hand; its camera features are Eye Contact, which corrects gaze, and Multicam, which switches between recorded angles. Crayo runs a face-aware camera track on every AutoClip clip, so a two-person podcast stays framed as speakers change, and the same track is editable with keyframes in the timeline editor. If you clip talking-head or podcast footage to 9:16, this is the row that decides.",
    },
    {
      question: "Which is better for podcasts, Crayo or Descript?",
      answer:
        "It depends on the job. For producing the episode, Descript: transcript editing, Studio Sound, filler-word removal and Multicam are made for long recordings, and the Mac and Windows apps handle big files locally. For turning that episode into shorts, Crayo: AutoClip ranks the moments, reframes faces, captions in 56 spoken languages and cuts up to 20 clips per run, with filler and silence cuts included in the clip's credit. Many podcasters will use both; this page is for the ones choosing one.",
    },
    {
      question: "Can I move from Descript to Crayo?",
      answer:
        `Yes, and nothing needs migrating. Export the episode from Descript or paste the YouTube link, upload it to AutoClip (up to 3 hours or 4 GB), pick a layout and intensity, and review the ranked clips. On Hobby you have ${hobby.credits} credits and ${hobby.exportMinutes} export minutes a month; Clipper raises that to ${clipper.credits} credits and ${clipper.exportMinutes} export minutes for ${money(clipper.monthly)} a month. Clip Cutter also takes manual ranges for free when you already know the moment you want. Your source files stay yours on both sides, and there is no project format to convert.`,
    },
    {
      question: "Does Crayo auto-post or schedule like Descript?",
      answer:
        "Neither tool schedules posts. Descript publishes to a shareable page and otherwise exports a file; Crayo exports a file for you to post yourself. Crayo's View Tracker follows Instagram, YouTube and TikTok creators for posting cadence and viral outliers, which is research, not scheduling or per-post analytics. Descript has no post analytics either. If native scheduling is your deciding feature, look at a clipper that includes one rather than either of these; Opus Clip is the usual example and has its own page in the related comparisons below.",
    },
    {
      question: "Can I cancel Descript or Crayo easily?",
      answer:
        "Crayo cancels in one click from Settings; the plan stays active to the end of the cycle and nothing further is charged. Descript's pricing page does not describe the cancellation steps, and its refund policy grants refunds only within 48 hours of the invoice with no proration. One dated Trustpilot review about cancelling is linked in the billing section of this page. On both sides, cancel before the renewal date to avoid a charge you cannot get back.",
    },
    {
      question: "Does Crayo also make story or text videos?",
      answer:
        "Yes, as extras on the same plan. Every Crayo plan also includes story-video, text-conversation, AI voiceover and AI image generators, each finished one costing 1 credit from the same monthly pool as clips. Descript has none of these; its pricing page lists AI image and video generation as credit-metered, checked Oct 8, 2026. None of this changes the clipping comparison above, but a clipper who occasionally needs a voiceover or a story video does not need a second subscription for it.",
    },
  ],
  changelog: [
    {
      date: "2026-10-09",
      text: "First published. Descript prices checked Oct 8, 2026: Hobbyist $24/mo or $16/mo billed annually with 10 media hours and 400 AI credits, Creator $35/mo or $24/mo annually with 4K, refunds only within 48 hours of the invoice.",
    },
    {
      date: "2026-10-25",
      text: `Crayo prices updated to the October 25, 2026 ladder: Hobby is ${crayoPlanTerms(hobby)}, with quarterly the default term at checkout. Verdicts and the corrections box re-checked at the new prices; competitor prices unchanged from the October 8 to 9 check.`,
    },
  ],
  relatedSpokes: ["crayo-vs-opus-clip", "crayo-vs-riverside-magic-clips", "crayo-vs-veed"],
  featurePath: "/features/auto-clip",
  ctaHref: "https://crayo.ai/create/auto-clip",
  ctaLabel: "Clip a video now",
  targetQueries: [
    "crayo vs descript",
    "descript alternative",
    "descript vs opus clip",
    "descript pricing changes",
    "descript alternative for shorts",
    "descript pricing",
  ],
};
